import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from '@mui/material';
import classes from './ReservationsHighlights.module.scss';
import { GET_RESERVATIONS_SUMMARY_REQUEST } from '../../../../../redux/actions';
import { isNumber } from '../../../../../utils/commonUtils';
import {
  L12M,
  L3060D,
  N30D,
  L30D,
} from '../../../../../utils/constants/filterConstants';
import { Highlight } from '../../../../common/BasicHighlight/BasicHighlight';
import {
  reservationsHighlightsErrorSelector,
  reservationsHighlightsLoadingSelector,
  reservationsHighlightsSelector,
} from '../../../../../redux/selectors/reservationsHighlightsSelector';
import { Loader } from '../../../../common/Loader/Loader';
import { isMobileModeSelector } from '../../../../../redux/selectors/appStatusSelector';
import { Typography } from '../../../../../shared/ui/typography';
import { reservationsSummarySelector } from '../../../../../redux/selectors/reservationsSummarySelector';

import { listingsSelector } from '@/redux/selectors/listingsSelector';
import { IListing } from '@/views/interfaces';
import { useMemo } from 'react';

interface IReservationsHighlights {
  period: string;
}

export const ReservationsHighlights = ({ period }: IReservationsHighlights) => {
  const dispatch = useDispatch();  
  const listings: IListing[] = useSelector(listingsSelector);
  const body = useMemo(() => {
    return (listings !== null ? listings : [])
      .filter((listing) => listing.is_listed)
      .map((listing) => listing.property_id);
  }, [listings]);  

  const isLoading = useSelector(reservationsHighlightsLoadingSelector);
  const isError = useSelector(reservationsHighlightsErrorSelector);
  const isMobileView = useSelector(isMobileModeSelector);

  const resrvationsData = useSelector(reservationsSummarySelector(period))?.summary?.[0];

  var uniqueReservations = null;
  var uniqueReservations_previous = null;
  var guestsHosted = null;
  var guestsHosted_previous = null;
  var averageStay = null;
  var averageStay_previous = null;
  var averageBookingWindow = null;
  var averageBookingWindow_previous = null;
  if(period == "last 12 months")
  {
    uniqueReservations = resrvationsData?.unique_reservations_YTD;
    uniqueReservations_previous = resrvationsData?.unique_reservations_YTD;
    guestsHosted = resrvationsData?.guest_hosted_YTD;
    guestsHosted_previous = resrvationsData?.guest_hosted_YTD;
    averageStay = resrvationsData?.length_of_stay_YTD;
    averageStay_previous = resrvationsData?.length_of_stay_YTD;
    averageBookingWindow = resrvationsData?.booking_window_YTD;
    averageBookingWindow_previous = resrvationsData?.booking_window_YTD;
  }else{
    uniqueReservations = resrvationsData?.unique_reservations_L30D;
    uniqueReservations_previous = resrvationsData?.unique_reservations_L30D;
    guestsHosted = resrvationsData?.guest_hosted_L30D;
    guestsHosted_previous = resrvationsData?.guest_hosted_L30D;
    averageStay = resrvationsData?.length_of_stay_L30D;
    averageStay_previous = resrvationsData?.length_of_stay_L30D;
    averageBookingWindow = resrvationsData?.booking_window_L30D;
    averageBookingWindow_previous = resrvationsData?.booking_window_L30D;
  }

  // console.log("ratingData: ", ratingData);

  useEffect(() => {
    dispatch({
      type: GET_RESERVATIONS_SUMMARY_REQUEST,
      payload: body,
    });
  }, [dispatch, body]);

  return (
    <div className={classes.reservationsHighlightsWrapper}>
      <>
        <Typography
          variant='text-xl'
          weight='medium'
          className={classes.gray900}
        >
          Reservations
        </Typography>
        <Loader isLoading={isLoading} isError={isError} data={resrvationsData} />
        {resrvationsData && (
          <div className={classes.highlights}>
            {uniqueReservations && (
              <>
                <Highlight
                  className={classes.highlight}
                  label='Unique Reservations'
                  tooltip={'Unique reservations are individual reservations'}
                  value={uniqueReservations}
                  previousValue={uniqueReservations_previous}
                  reverse={isMobileView}
                  showDynamicIndicator={period !== N30D && period !== L30D}
                />
              </>
            )}
            {isNumber(guestsHosted) && (
              <>
                <Highlight
                  className={classes.highlight}
                  label='Guests Hosted'
                  tooltip={
                    'This number indicates the total number of guests you have hosted at this property in the last period'
                  }
                  value={guestsHosted}
                  previousValue={guestsHosted_previous ?? 0}
                  reverse={isMobileView}
                  showDynamicIndicator={period !== N30D && period !== L30D}
                />
              </>
            )}
            {isNumber(Number(averageStay)) && (
              <>
                <Highlight
                  className={classes.highlight}
                  label='Average Length of Stay'
                  tooltip={
                    'This is the average amount of time a guest/(s) has stayed at your property in a given reservation'
                  }
                  value={averageStay}
                  previousValue={averageStay_previous}
                  reverse={isMobileView}
                  showDynamicIndicator={period !== N30D && period !== L30D}
                />
              </>
            )}
            {isNumber(Number(averageBookingWindow)) && (
              <Highlight
                className={classes.highlight}
                label='Average Booking Window'
                tooltip={
                  'This the average period of time between when a reservation is made by the guest and their check-in date'
                }
                value={averageBookingWindow}
                previousValue={
                  averageBookingWindow_previous
                }
                reverse={isMobileView}
                showDynamicIndicator={period !== N30D && period !== L30D}
              />
            )}
          </div>
        )}
      </>
    </div>
  );
};
